/**
 * Lecture des variables d'environnement exposees au navigateur.
 *
 * Aucune valeur de repli : une configuration absente est une erreur qui doit
 * etre visible immediatement, pas une valeur par defaut silencieuse.
 *
 * Le garde-fou contre les cles secretes n'est volontairement pas ici : il vit
 * dans scripts/check-no-secrets.sh, qui interdit sans exception les motifs de
 * cle secrete dans src/. Les y ecrire, meme comme motif de detection, rendrait
 * ce controle inoperant.
 */

export interface SupabaseEnv {
  url: string
  publishableKey: string
}

export class EnvError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EnvError'
  }
}

/**
 * Valide et normalise les variables Supabase.
 *
 * @throws {EnvError} si une variable manque ou est vide.
 */
export function readSupabaseEnv(env: Partial<Record<string, string>>): SupabaseEnv {
  const url = env.VITE_SUPABASE_URL?.trim()
  const publishableKey = env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim()

  if (!url) {
    throw new EnvError('VITE_SUPABASE_URL est absente. Voir .env.example.')
  }
  if (!publishableKey) {
    throw new EnvError('VITE_SUPABASE_PUBLISHABLE_KEY est absente. Voir .env.example.')
  }

  return { url, publishableKey }
}

/** Valeur qui, et seulement qui, designe la production. */
export const ENVIRONNEMENT_PRODUCTION = 'production'

/**
 * Faut-il avertir que l'application ne tourne pas en production ?
 *
 * LE DEFAUT EST L'AVERTISSEMENT. Variable absente, vide, mal orthographiee ou
 * differente : le bandeau s'affiche. Un oubli de configuration doit produire un
 * avertissement en trop, jamais un avertissement en moins — l'erreur couteuse
 * est de presenter des donnees fabriquees comme si elles etaient reelles.
 *
 * La comparaison est stricte, sans normalisation de casse : « Production »
 * n'est pas « production ». Accepter les variantes ferait dependre la mention
 * d'une equivalence que personne n'a ecrite quelque part.
 */
export function estPreproduction(env: Partial<Record<string, string>>): boolean {
  return (env.VITE_ENVIRONNEMENT ?? '').trim() !== ENVIRONNEMENT_PRODUCTION
}
